import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { io } from 'socket.io-client';
import { logout, getToken, getSessionId, getTabId } from '../../utils/auth';
import { toast } from 'react-toastify'; 

const SocketContext = createContext(null);

export const SocketProvider = ({ children }) => {
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [isPainelConnected, setIsPainelConnected] = useState(false);
  const [isSessionConnected, setIsSessionConnected] = useState(false);
  
  // Refs para manter a instância estável e evitar múltiplas conexões no StrictMode
  const painelSocketRef = useRef(null);
  const sessionSocketRef = useRef(null);

  useEffect(() => {
    if (painelSocketRef.current) return; // Evita duplicidade

    const rawBase = window.env?.REACT_APP_SOCKET || process.env.REACT_APP_SOCKET || window.location.origin;
    const base = rawBase.replace(/\/$/, '');
    const path = process.env.REACT_APP_SOCKET_PATH || '/socket.io/';
    const ns = process.env.REACT_APP_SOCKET_NS || '/ws';

    const commonOpts = {
      path,
      transports: ['websocket', 'polling'],
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      autoConnect: !!getToken(),
      auth: { token: getToken(), tabId: getTabId() },
    };

    // 1. SETUP PAINEL
    const painel = io(base, commonOpts);
    painelSocketRef.current = painel;

    // 2. SETUP SESSION
    const session = io(`${base}${ns}`, commonOpts);
    sessionSocketRef.current = session;

    // Handlers de Status
    const setStatus = (p, s) => {
      setIsPainelConnected(p);
      setIsSessionConnected(s);
    };

    // Listeners do Painel
    painel.on('connect', () => setIsPainelConnected(true));
    painel.on('disconnect', () => setIsPainelConnected(false));
    painel.on('connect_error', () => setIsPainelConnected(false));

    // Listeners de Sessão
    session.on('connect', () => {
      setIsSessionConnected(true);
      session.emit('register', { token: getToken(), tabId: getTabId() });
    });
    session.on('disconnect', () => setIsSessionConnected(false));
    session.on('connect_error', () => setIsSessionConnected(false));
    
    session.on('online_users', (users) => setOnlineUsers(users || []));
    
    session.on('session_force_logout', ({ newSessionId, tabId: kickerTabId }) => {
      if (kickerTabId === getTabId()) return;
      const mySessionId = getSessionId();

      if (!mySessionId || (newSessionId && mySessionId !== newSessionId)) {
        painel.disconnect();
        session.disconnect();
        logout();
        toast.warning("Sessão encerrada: Login detectado em outro local.", { autoClose: false });
        window.location.href = '/login';
      }
    });

    // Sincronização entre abas
    const handleTokenUpdate = () => {
      const t = getToken();
      const tabId = getTabId();
      painel.auth = { token: t, tabId };
      session.auth = { token: t, tabId };

      if (t) {
        if (painel.disconnected) painel.connect();
        if (session.disconnected) session.connect();
      } else {
        painel.disconnect();
        session.disconnect();
        setOnlineUsers([]);
        setStatus(false, false);
      }
    };

    window.addEventListener('storage', handleTokenUpdate);
    window.addEventListener('token-updated', handleTokenUpdate);

    return () => {
      window.removeEventListener('storage', handleTokenUpdate);
      window.removeEventListener('token-updated', handleTokenUpdate);
      painel.close();
      session.close();
      painelSocketRef.current = null;
      sessionSocketRef.current = null;
    };
  }, []);

  return (
    <SocketContext.Provider value={{ 
      painelSocket: painelSocketRef.current, 
      sessionSocket: sessionSocketRef.current, 
      onlineUsers,
      isPainelConnected,
      isSessionConnected
    }}>
      {children}
    </SocketContext.Provider>
  );
};
// Hooks
export const usePainelSocket = () => {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error('usePainelSocket precisa do SocketProvider');
  return ctx.painelSocket;
};

export const useSessionSocket = () => {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error('useSessionSocket precisa do SocketProvider');
  return ctx.sessionSocket;
};

export const useOnlineUsers = () => {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error('useOnlineUsers precisa do SocketProvider');
  return ctx.onlineUsers;
};

// Retrocompatibilidade
export const useSocket = usePainelSocket;

// NOVO HOOK: Para saber se está conectado (Use isso no UserInfo)
export const useSocketStatus = () => {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error('useSocketStatus precisa do SocketProvider');
  return { 
    connected: ctx.isSessionConnected, // Status geral da sessão
    painelConnected: ctx.isPainelConnected 
  };
};